/**
 * knowledgeApi — vibe-designer 1.169.0 Phase 2.
 *
 * Thin wrappers around the new /api/knowledge/* backend routes. Mirrors
 * the existing per-feature API modules (configStore→config endpoint,
 * agentService→agent endpoints) — routes through services/api.ts so
 * Authorization: Bearer is auto-supplied (no bare fetch).
 */
import { api } from './api';

/**
 * Canonical wire shape for a single service's stats. Mirrors the backend
 * TypedDict at services/knowledge_stats.py.
 */
export interface KnowledgeServiceStats {
  service: 'mind' | 'cortex' | 'brain' | 'sight' | 'cognition' | 'obsidian';
  available: boolean;
  count: number | null;
  label: string | null;
  secondary: Record<string, number> | null;
  error: string | null;
}

export interface KnowledgeOverviewResponse {
  tenant_id: string;
  services: KnowledgeServiceStats[];
}

/**
 * GET /api/knowledge/overview. Returns per-tenant stats for all 6
 * knowledge services in deterministic order.
 */
export async function fetchOverview(): Promise<KnowledgeOverviewResponse> {
  return api.get<KnowledgeOverviewResponse>('/knowledge/overview');
}

/**
 * 1.170.0 Phase 3 — per-service search.
 *
 * Single normalized hit shape across all 6 services. `score` is null for
 * services without native similarity scoring (e.g., CORTEX/Graphiti).
 * `metadata` carries service-specific extras (uuid + valid_at for CORTEX,
 * collection name for MIND/BRAIN/SIGHT, etc.) — null when empty.
 */
export interface KnowledgeSearchHit {
  title: string;
  snippet: string;
  score: number | null;
  source: string | null;
  metadata: Record<string, unknown> | null;
}

/**
 * Canonical wire shape returned by GET /api/knowledge/services/{name}/search.
 * Mirrors backend `services/knowledge_search.py:KnowledgeSearchResponse`.
 */
export interface KnowledgeSearchResponse {
  service: KnowledgeServiceStats['service'];
  query: string;
  available: boolean;
  hits: KnowledgeSearchHit[];
  error: string | null;
}

// ============================================================================
// 1.172.0 Phase 5α — Knowledge ingest (files + text → MIND/CORTEX/OBSIDIAN)
// ============================================================================

// 'processing' (2026-06-28) is the non-terminal sentinel for an async scanned-PDF
// OCR job — the SPA polls GET /knowledge/ingest/{job_id}/status until terminal.
export type IngestStatus = 'completed' | 'partial' | 'failed' | 'skipped' | 'processing';

export interface IngestServiceResult {
  service: KnowledgeServiceStats['service'];
  status: IngestStatus;
  chunks_created: number;
  job_id: string | null;
  error: string | null;
  duration_ms: number;
}

export interface IngestRequest {
  content: string;
  source_name: string;
  target_services?: KnowledgeServiceStats['service'][];
  collection?: string;
  metadata?: Record<string, unknown>;
  content_type?: 'text' | 'markdown' | 'image';
}

export interface IngestResponse {
  job_id: string;
  tenant_id: string;
  source_name: string;
  started_at: string;
  finished_at: string | null;
  per_service: IngestServiceResult[];
  // 'processing' = async scanned-PDF OCR job in flight; poll the status route.
  overall_status: 'completed' | 'partial' | 'failed' | 'processing';
}

/** Terminal overall_status values (everything except the async 'processing'). */
export function isTerminalIngest(resp: IngestResponse): boolean {
  return resp.overall_status !== 'processing';
}

/**
 * GET /api/knowledge/ingest/{job_id}/status — poll an ingest job. Used by the
 * async scanned-PDF OCR lane: the POST returns overall_status='processing' + a
 * kp- job_id, and the SPA polls this until terminal. A 404 (ApiError status 404)
 * means the job was lost (e.g. backend restart) — the caller surfaces "re-submit".
 */
export async function getIngestJobStatus(jobId: string): Promise<IngestResponse> {
  return api.get<IngestResponse>(`/knowledge/ingest/${encodeURIComponent(jobId)}/status`);
}

export interface OcrHealthResponse {
  available: boolean;
  queue_depth: number | null;
  mem_available_gib: number | null;
}

/**
 * GET /api/knowledge/ocr/health — passthrough of the DGX1 marker-ocr /health for
 * the scanned-PDF queue-position UX. `available` is derived server-side
 * (marker-ocr /health has no such field); honest-degrades to available:false.
 */
export async function getOcrHealth(): Promise<OcrHealthResponse> {
  return api.get<OcrHealthResponse>('/knowledge/ocr/health');
}

// ----------------------------------------------------------------------------
// 1.173.0 Phase 5β1 — Monitoring routes
// ----------------------------------------------------------------------------

export interface IngestRecentResponse {
  jobs: IngestResponse[];
  total: number;
}

/**
 * GET /api/knowledge/ingest/recent — most-recent ingest jobs from the
 * in-memory cache (single-worker uvicorn; resets on container restart).
 */
export async function fetchRecentIngestRuns(limit = 20): Promise<IngestRecentResponse> {
  return api.get<IngestRecentResponse>(`/knowledge/ingest/recent?limit=${limit}`);
}

export interface ObsidianPage {
  path: string;
  size: number;
  mtime_epoch: number;
}

export interface ObsidianPageListResponse {
  pages: ObsidianPage[];
  total: number;
  error: string | null;
}

/**
 * GET /api/knowledge/services/obsidian/page-list — list .md pages under
 * /space on the per-tenant SilverBullet.
 */
export async function fetchObsidianPageList(limit = 50): Promise<ObsidianPageListResponse> {
  return api.get<ObsidianPageListResponse>(`/knowledge/services/obsidian/page-list?limit=${limit}`);
}

// ----------------------------------------------------------------------------

/**
 * POST /api/knowledge/ingest — fan-out write to target_services.
 *
 * BRAIN + COGNITION + SIGHT are deferred (Phase 5α scope); the backend
 * returns status=skipped + honest error if any of those are in target_services.
 *
 * Returns the IngestResponse after all target services have completed
 * (or failed/skipped). The 20MB body cap (OQ-5.3) is enforced by Pydantic
 * max_length on the backend.
 */
export async function ingestKnowledge(req: IngestRequest): Promise<IngestResponse> {
  return api.post<IngestResponse>('/knowledge/ingest', req);
}

/**
 * POST /api/knowledge/ingest/pdf — multipart PDF upload (2026-06-28).
 *
 * The backend extracts the PDF text layer (pypdf) server-side, then ingests it
 * into the target services exactly like a text ingest. Routes through
 * api.postForm so the browser sets the multipart boundary while Bearer + the
 * 401 refresh-retry are still applied. Returns the standard IngestResponse.
 */
export async function ingestPdf(args: {
  file: File;
  source_name?: string;
  target_services?: string[];
  collection?: string;
  pillar?: string | null;
}): Promise<IngestResponse> {
  const form = new FormData();
  form.append('file', args.file, args.file.name);
  form.append('collection', args.collection ?? 'docs');
  form.append('target_services', (args.target_services ?? ['mind']).join(','));
  if (args.source_name) form.append('source_name', args.source_name);
  if (args.pillar) form.append('pillar', args.pillar);
  return api.postForm<IngestResponse>('/knowledge/ingest/pdf', form);
}

/**
 * GET /api/knowledge/services/{service}/search?q=&limit=&pillar=. Backend
 * clamps limit to 50 server-side. `pillar` is MIND-only enforced; other
 * services accept the kwarg as a Protocol no-op (1.184.0 Phase 7.1).
 */
export async function searchService(
  service: KnowledgeServiceStats['service'],
  q: string,
  limit = 10,
  opts?: { pillar_filter?: string | null },
): Promise<KnowledgeSearchResponse> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  if (opts?.pillar_filter) {
    params.set('pillar', opts.pillar_filter);
  }
  return api.get<KnowledgeSearchResponse>(
    `/knowledge/services/${service}/search?${params.toString()}`,
  );
}

// ============================================================================
// 1.174.0 Phase 5β2 — MIND stats-extended + CORTEX graph viz
// ============================================================================

export interface MindChunksPerDayPoint {
  date: string; // YYYY-MM-DD UTC
  count: number;
}

export interface MindPerTableBreakdown {
  mind_docs: number;
  mind_configs: number;
  mind_code: number;
  mind_sessions: number;
}

export interface MindStatsExtendedResponse {
  available: boolean;
  chunks_per_day_30d: MindChunksPerDayPoint[];
  per_table_breakdown: MindPerTableBreakdown;
  error: string | null;
}

/**
 * GET /api/knowledge/services/mind/stats-extended — per-table breakdown +
 * 30-day chunks-per-day timeline. Backend reads pgvector directly via
 * MindStore. Honest-empty when pgvector unconfigured.
 */
export async function fetchMindStatsExtended(): Promise<MindStatsExtendedResponse> {
  return api.get<MindStatsExtendedResponse>('/knowledge/services/mind/stats-extended');
}

export interface CortexGraphNode {
  id: string;
  name: string;
  group: string;
  edge_degree: number;
}

export interface CortexGraphEdge {
  source: string;
  target: string;
  label: string;
  uuid: string | null;
}

export interface CortexGraphResponse {
  nodes: CortexGraphNode[];
  edges: CortexGraphEdge[];
  error: string | null;
}

/**
 * GET /api/knowledge/services/cortex/graph?limit=50 — entity/fact graph
 * for the force-graph viz. Backend clamps limit to 500 server-side.
 * Empty graph (zero nodes) + error=null is the happy-empty path for
 * tenants with no CORTEX data.
 */
export async function fetchCortexGraph(limit = 50): Promise<CortexGraphResponse> {
  return api.get<CortexGraphResponse>(`/knowledge/services/cortex/graph?limit=${limit}`);
}

// WS-B1 (2026-07-12) — MIND document-level kNN similarity graph. Extends the
// CortexGraphNode shape ({id,name,group,edge_degree}) with optional UMAP 2D
// semantic-map coords (x, y) — present when services.mind_coord_builder has
// built them for the tenant (the frontend then plots fixed positions instead of
// a force layout), null/absent otherwise. Edges carry a similarity label
// ("sim 0.NN") and no uuid. Same {nodes,edges,error} envelope as CORTEX.
export interface MindGraphNode extends CortexGraphNode {
  x?: number | null;
  y?: number | null;
}

export interface MindGraphEdge {
  source: string;
  target: string;
  label: string;
  // Numeric similarity 0..1 (= 1 - cosine distance) — drives the strength slider.
  weight?: number | null;
}

export interface MindGraphResponse {
  nodes: MindGraphNode[];
  edges: MindGraphEdge[];
  error: string | null;
}

/**
 * GET /api/knowledge/services/mind/graph?limit=60000 — document-level
 * similarity graph from precomputed kNN edges (mind_doc_edges). EDGE-DRIVEN:
 * `limit` is an edge budget; nodes = the union of those edges' endpoints (no
 * stranded dots). The default (60000) requests the COMPLETE kNN web — a low
 * budget is degenerate here (this corpus's strongest edges are ~3,690 exact-
 * duplicate sim-1.00 pairs, so a small budget shows only dupes). Backend clamps
 * to 60000. Empty graph + error=null is the happy-empty path. See the styling
 * guide: docs/20260724/20260724_KNOWLEDGE_GRAPH_VIZ_STYLING_GUIDE.md
 */
export async function fetchMindGraph(limit = 60000): Promise<MindGraphResponse> {
  return api.get<MindGraphResponse>(`/knowledge/services/mind/graph?limit=${limit}`);
}

// ============================================================================
// 1.178.0 Phase 5γ.2 — SIGHT image grid + per-item image fetch
// ============================================================================

export interface SightRecentItem {
  id: string;
  content: string;
  source_file: string | null;
  embedding_type: string;
  has_image: boolean;
  updated_at: string | null;
}

export interface SightRecentResponse {
  items: SightRecentItem[];
  total: number;
  error: string | null;
}

/**
 * GET /api/knowledge/services/sight/recent?limit=24 — metadata-only
 * (R1 C2: full data URIs NOT in this response; per-item fetch on demand).
 */
export async function fetchSightRecent(limit = 24): Promise<SightRecentResponse> {
  return api.get<SightRecentResponse>(`/knowledge/services/sight/recent?limit=${limit}`);
}

export interface SightItemImageResponse {
  image_url: string | null;
  error: string | null;
}

/**
 * GET /api/knowledge/services/sight/items/{id}/image — full data URI
 * for one row (tenant-scoped server-side).
 */
export async function fetchSightItemImage(itemId: string): Promise<SightItemImageResponse> {
  return api.get<SightItemImageResponse>(
    `/knowledge/services/sight/items/${encodeURIComponent(itemId)}/image`,
  );
}

// ============================================================================
// 1.177.0 Phase 6.1 — URL ingest
// ============================================================================

export interface IngestUrlRequest {
  url: string;
  target_services?: KnowledgeServiceStats['service'][];
  collection?: string;
  metadata?: Record<string, unknown>;
}

/**
 * POST /api/knowledge/ingest/url — fetch a public URL, extract text via
 * stdlib HTML parser, delegate to ingest_now. SSRF-guarded: hostname
 * DNS-resolved + private/loopback/link-local IPs rejected before fetch.
 * Returns the same IngestResponse shape as POST /api/knowledge/ingest.
 */
export async function ingestUrl(req: IngestUrlRequest): Promise<IngestResponse> {
  return api.post<IngestResponse>('/knowledge/ingest/url', req);
}

// ============================================================================
// 1.179.0 Phase 6.2.1 — code-repo ingest
// ============================================================================

export interface IngestRepoRequest {
  git_url: string;
  branch?: string;
  max_files?: number;
  path_filters?: string[];
  target_services?: KnowledgeServiceStats['service'][];
  collection?: string;
  metadata?: Record<string, unknown>;
}

/**
 * POST /api/knowledge/ingest/repo — clone a public HTTPS git repo
 * (--depth=1 --single-branch --filter=blob:limit=200k), walk text files,
 * per-file delegate to ingest_now (with skip_cache=true so per-file inner
 * entries don't crowd Recent Ingests), aggregate to a single `kr-*`
 * IngestResponse. Public-only scope: internal Forgejo is rejected by
 * the SSRF guard by design (Phase 6.2.2+ would carve that out).
 */
export async function ingestRepo(req: IngestRepoRequest): Promise<IngestResponse> {
  return api.post<IngestResponse>('/knowledge/ingest/repo', req);
}

// ============================================================================
// 1.180.0 Phase 6.3 — Activity feed SSE
// ============================================================================

export type ActivityEventType = 'ingest' | 'url' | 'repo' | 'image' | 'system';

export interface ActivityEvent {
  event_id: string;
  event_type: ActivityEventType;
  tenant_id: string;
  timestamp: string;
  job_id: string | null;
  source_name: string | null;
  overall_status: 'completed' | 'partial' | 'failed' | null;
  chunks_created: number;
  services: string[];
  error: string | null;
}

// ============================================================================
// 1.175.0 Phase 5β3 — OBSIDIAN [[wikilink]] backlink graph
// ============================================================================

export interface ObsidianGraphNode {
  id: string; // page path, e.g., "index.md"
  name: string;
  group: 'Page' | 'Orphan';
  edge_degree: number;
}

export interface ObsidianGraphEdge {
  source: string;
  target: string;
  label: string;
}

export interface ObsidianGraphResponse {
  nodes: ObsidianGraphNode[];
  edges: ObsidianGraphEdge[];
  error: string | null;
}

/**
 * GET /api/knowledge/services/obsidian/graph?limit=100 — parses
 * [[wikilink]] references from every .md page under /space and builds
 * a force-graph-ready node+edge structure. Backend clamps limit to 500.
 * Orphan-target wikilinks render as group='Orphan' nodes so dangling
 * references are visible in the graph.
 */
export async function fetchObsidianGraph(limit = 100): Promise<ObsidianGraphResponse> {
  return api.get<ObsidianGraphResponse>(`/knowledge/services/obsidian/graph?limit=${limit}`);
}

// ============================================================================
// 1.171.0 Phase 4 — Ask box + All-services search
// ============================================================================

export interface KnowledgeAskCitation {
  service: KnowledgeServiceStats['service'];
  title: string;
  source: string | null;
  snippet: string;
}

export interface KnowledgeSourceSummary {
  service: KnowledgeServiceStats['service'];
  available: boolean;
  hits_used: number;
  error: string | null;
}

export interface KnowledgeAskResponse {
  q: string;
  answer: string | null;
  citations: KnowledgeAskCitation[];
  sources_consulted: KnowledgeSourceSummary[];
  error: string | null;
}

export interface KnowledgeAskRequest {
  q: string;
  services?: KnowledgeServiceStats['service'][];
  limit_per_service?: number;
  // 1.184.0 Phase 7.1 — forwarded server-side via knowledge_ask.py;
  // MIND-only enforcement at pgvector layer (others no-op).
  pillar_filter?: string;
  // 1.196.0 Phase 8.3 — UI parity with Pi chat: per-query model selector +
  // thinking/reasoning toggle. Both optional; omitted = backend default.
  model?: string;
  thinking?: boolean;
}

/**
 * POST /api/knowledge/ask — multi-source LLM Q&A with citations.
 * Honest fallback: returns answer=null + error string if LLM raised or timed out.
 */
export async function askKnowledge(
  q: string,
  opts?: {
    services?: KnowledgeServiceStats['service'][];
    limit_per_service?: number;
    pillar_filter?: string | null;
    // 1.196.0 Phase 8.3 — per-query model + thinking overrides.
    model?: string;
    thinking?: boolean;
  },
): Promise<KnowledgeAskResponse> {
  const body: KnowledgeAskRequest = {
    q,
    ...(opts?.services ? { services: opts.services } : {}),
    ...(opts?.limit_per_service != null ? { limit_per_service: opts.limit_per_service } : {}),
    ...(opts?.pillar_filter ? { pillar_filter: opts.pillar_filter } : {}),
    // R1-C3 lock: conditional spread per-field — interface field alone
    // isn't enough; body construction must include each. Truthy guards
    // omit '' (default-model placeholder) and false (thinking off).
    ...(opts?.model ? { model: opts.model } : {}),
    ...(opts?.thinking ? { thinking: opts.thinking } : {}),
  };
  return api.post<KnowledgeAskResponse>('/knowledge/ask', body);
}

export interface KnowledgeAllSearchResponse {
  q: string;
  results_by_service: Record<KnowledgeServiceStats['service'], KnowledgeSearchResponse>;
  total_hits: number;
}

/**
 * GET /api/knowledge/services/all/search?q=&limit_per_service=. Parallel fan-out
 * across all 6 services; backend clamps limit_per_service to 20 server-side.
 */
export async function searchAllServices(
  q: string,
  limit_per_service = 5,
  opts?: { pillar_filter?: string | null },
): Promise<KnowledgeAllSearchResponse> {
  const params = new URLSearchParams({ q, limit_per_service: String(limit_per_service) });
  if (opts?.pillar_filter) {
    params.set('pillar', opts.pillar_filter);
  }
  return api.get<KnowledgeAllSearchResponse>(
    `/knowledge/services/all/search?${params.toString()}`,
  );
}


// ============================================================================
// 1.185.0 Phase 7.2 — Web Search as 7th service surface (SearXNG)
// ============================================================================

export interface WebSearchResult {
  title: string;
  url: string;
  snippet: string;
  engine: string;
}

export interface WebSearchResponse {
  query: string;
  results: WebSearchResult[];
}

/**
 * POST /api/web/search — SearXNG-backed web search. Backend gracefully
 * degrades to {results: []} on SearXNG timeout / non-200 / non-JSON, never
 * raises. Empty list could mean "no hits" OR "SearXNG down" — pair with
 * `mcpCapabilities.web_search` from tenantStore for the honest signal.
 */
export async function runWebSearch(
  q: string,
  max_results = 5,
): Promise<WebSearchResponse> {
  return api.post<WebSearchResponse>('/web/search', { query: q, max_results });
}

// ============================================================================
// 1.190.0 Phase 7.6 — MIND pillar inventory in Overview
// ============================================================================

export interface MindPillarEntry {
  /** Pillar name; null for bulk-ingested platform-ops docs (§5.3 reframe). */
  pillar: string | null;
  count: number;
}

export interface MindPillarBreakdownResponse {
  available: boolean;
  pillars: MindPillarEntry[];
  error: string | null;
}

/**
 * GET /api/knowledge/services/mind/pillars — pillar group-by for the
 * Knowledge tab Overview's MindPillarInventory card.
 * Backend reads pgvector directly (mind_docs.pillar GROUP BY); honest-empty
 * when MIND has zero rows OR pgvector unconfigured. Pair `available=false`
 * with the `error` field for the user-visible reason.
 */
export async function fetchMindPillarBreakdown(): Promise<MindPillarBreakdownResponse> {
  return api.get<MindPillarBreakdownResponse>('/knowledge/services/mind/pillars');
}

// ============================================================================
// 2026-07-02 — THE SYNAPSE (knowledge loop) status + manual compile
// ============================================================================

/**
 * Wire shape of GET /api/knowledge/synapse/status. Mirrors
 * backend services/synapse_status.SynapseStatusService.status().
 * SYNAPSE is the knowledge LOOP (wiki compiler), not a corpus — it has
 * no entry in /overview and no search/ingest surface; its state is the
 * compile engine's reachability + the wiki index the compiler rewrites.
 */
export interface SynapseStatusResponse {
  /** Compile engine (per-tenant mind-chat /v1/health) reachable. */
  available: boolean;
  /** Tenant wiki (SilverBullet) reachable — where compiled articles live. */
  wiki_available: boolean;
  /** ISO timestamp from the index page's "Auto-compiled" marker; null = never compiled. */
  last_compiled_at: string | null;
  /** Compiled-article count (null when the wiki is unreadable). */
  article_count: number | null;
  /** Article slugs (server-capped list; count above is the full total). */
  articles: string[];
  error: string | null;
}

export async function fetchSynapseStatus(): Promise<SynapseStatusResponse> {
  return api.get<SynapseStatusResponse>('/knowledge/synapse/status');
}

/** Wire shape of POST /api/knowledge/synapse/compile. */
export interface SynapseCompileResponse {
  ok: boolean;
  articles: string[];
  errors: unknown[];
  error: string | null;
}

/**
 * Trigger a SYNAPSE compile run for this tenant (same pipeline as the
 * 3 AM cron). Server owns tenant identity; only optional topics cross the
 * wire. Long-running (LLM synthesis) — backend budget is 300s.
 */
export async function compileSynapse(topics?: string[]): Promise<SynapseCompileResponse> {
  return api.post<SynapseCompileResponse>('/knowledge/synapse/compile', {
    topics: topics ?? null,
  });
}
